import type { AccessLevelProp, BadgeProps, IconName } from '@meterup/atto';
import type { ExtractRouteParams } from '@meterup/react-router-extensions';
import type { HTMLAttributeAnchorTarget, ReactNode } from 'react';
import {
  AccessLevel,
  AlignStack,
  Badge,
  Body,
  colors,
  darkThemeSelector,
  Divider,
  fade,
  FeatureBadge,
  Icon,
  palette,
  shadows,
  Small,
  styled,
  Text,
  transitions,
  useAccessLevelContext,
  UserAccessLevelContextProvider,
  UtilityControl,
  zIndexes,
} from '@meterup/atto';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { mergeRefs } from 'react-merge-refs';
import { type To } from 'react-router';

import { dockValues } from '../shared';
import { useNavigationGroupsContext } from './utils';

const NavigationDividerElement = styled(Divider, {
  padding: '$4',
});
NavigationDividerElement.displayName = 'NavigationDividerElement';

export function NavigationDivider({ minimized }: { minimized: boolean }) {
  return (
    <NavigationDividerElement
      appearance={minimized ? 'thick' : 'thin'}
      direction="row"
      variant="neutral"
    />
  );
}

const navigationSectionStuckShadowLight = `inset 0 11px 0 -10px ${fade(palette.gray800, 0.04)}, inset 0 12px 3px -10px ${fade(palette.gray800, 0.04)}`;
const navigationSectionStuckShadowDark = `inset 0 11px 0 -10px ${fade(palette.gray700, 0.8)}, inset 0 11px 1px -10px ${fade(palette.gray900, 0.8)}, inset 0 12px 3px -10px ${fade(palette.gray900, 0.8)}`;

const NavigationSectionFrame = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: colors.bgApplicationLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },

  variants: {
    gap: {
      true: {
        gap: '$4',
      },
      false: {},
    },
    minimized: {
      true: {
        gap: '$2',
      },
      false: {},
    },
    surface: {
      true: {},
      false: {},
    },
    stuck: {
      true: {
        zIndex: 2,
        margin: '-$4 0.5px 0 0',
        padding: '$8 $8 $4',

        '&::after': {
          content: '',
          position: 'absolute',
          right: 0,
          bottom: '-$8',
          left: 0,
          display: 'block',
          height: '$8',
          boxShadow: navigationSectionStuckShadowLight,

          [darkThemeSelector]: {
            boxShadow: navigationSectionStuckShadowDark,
          },
        },
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      gap: false,
      minimized: true,
      css: {
        gap: '$4',
      },
    },
  ],
});
NavigationSectionFrame.displayName = 'NavigationSectionFrame';

const NavigationSectionStickyShell = styled('div', {
  position: 'fixed',
  zIndex: zIndexes.drawerSticky,
  left: 0,
  height: 0,
  opacity: 0,
  transition: 'opacity 150ms ease-out',
  pointerEvents: 'none',

  '@tablet': {
    width: `$${dockValues.width.maximized['@tablet']}`,
  },

  '@desktop': {
    width: `$${dockValues.width.maximized['@desktop']}`,
  },

  variants: {
    visible: {
      true: {
        opacity: 1,
        pointerEvents: 'auto',
      },
      false: {
        opacity: 0,
        pointerEvents: 'none',
      },
    },
  },
});
NavigationSectionStickyShell.displayName = 'NavigationSectionStickyShell';

type NavigationSectionFrameProps = React.ComponentProps<typeof NavigationSectionFrame>;

function useStickyNavigationSection(enabled: boolean, ref: React.RefObject<HTMLDivElement | null>) {
  const [isStuck, setIsStuck] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const node = ref.current;
    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!ref.current?.clientHeight) {
          return;
        }

        const [entry] = entries;
        setIsStuck(!entry.isIntersecting);
      },
      { threshold: 0.3, rootMargin: '0px 0px -1px 0px' },
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
  }, [enabled, ref]);

  return { isStuck };
}

type NavigationSectionProps = Omit<NavigationSectionFrameProps, 'surface' | 'stuck'> & {
  sticky?: boolean;
};

const NavigationSectionSticky = memo(
  forwardRef<HTMLDivElement, Omit<NavigationSectionProps, 'sticky'>>(
    ({ children, minimized, ...rest }, forwardedRef) => {
      const ref = useRef<HTMLDivElement>(null);
      const { isStuck } = useStickyNavigationSection(!minimized, ref);
      const stickyShellRef = useRef<HTMLDivElement>(null);
      const stickyFrameRef = useRef<HTMLDivElement>(null);
      const combinedRef = mergeRefs([forwardedRef, ref]);
      const frameProps = rest as NavigationSectionFrameProps;

      useLayoutEffect(() => {
        // Only apply sticky positioning when dock is not minimized
        if (minimized) {
          return undefined;
        }

        const frame = stickyFrameRef.current;
        const originalSection = ref.current;
        if (!frame || !originalSection) {
          return undefined;
        }

        // Find the scroll container once
        const findScrollContainer = (): HTMLElement | null => {
          let container: HTMLElement | null = originalSection;
          while (container && container !== document.body) {
            const style = window.getComputedStyle(container);
            if (
              style.overflowY === 'auto' ||
              style.overflowY === 'scroll' ||
              style.overflowY === 'overlay'
            ) {
              return container;
            }
            container = container.parentElement;
          }
          return null;
        };

        const scrollContainer = findScrollContainer();

        // Update fixed position based on scroll container's position
        const updateFixedPosition = () => {
          const shell = stickyShellRef.current;
          if (!shell || !isStuck || !scrollContainer) {
            return;
          }

          const scrollRect = scrollContainer.getBoundingClientRect();
          shell.style.top = `${scrollRect.top}px`;
          shell.style.left = `${scrollRect.left}px`;
          shell.style.width = `${scrollRect.width}px`;
        };

        updateFixedPosition();

        // Watch for size changes
        const resizeObserver = new ResizeObserver(() => {
          updateFixedPosition();
        });
        resizeObserver.observe(frame);
        resizeObserver.observe(originalSection);
        if (scrollContainer) {
          resizeObserver.observe(scrollContainer);
        }

        // Update position on scroll and resize
        const handleScroll = () => {
          updateFixedPosition();
        };
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', updateFixedPosition);
        if (scrollContainer) {
          scrollContainer.addEventListener('scroll', handleScroll);
        }

        return () => {
          resizeObserver.disconnect();
          window.removeEventListener('scroll', handleScroll, true);
          window.removeEventListener('resize', updateFixedPosition);
          if (scrollContainer) {
            scrollContainer.removeEventListener('scroll', handleScroll);
          }
        };
      }, [isStuck, minimized, ref]);

      return (
        <>
          <NavigationSectionFrame
            ref={combinedRef}
            {...frameProps}
            minimized={minimized}
            data-sticky-section
          >
            {children}
          </NavigationSectionFrame>
          <NavigationSectionStickyShell
            ref={stickyShellRef}
            visible={!minimized && isStuck}
            aria-hidden="true"
          >
            <NavigationSectionFrame
              ref={stickyFrameRef}
              data-sticky-frame
              stuck
              {...frameProps}
              minimized={minimized}
            >
              {children}
            </NavigationSectionFrame>
          </NavigationSectionStickyShell>
        </>
      );
    },
  ),
);

export const NavigationSection = forwardRef<HTMLDivElement, NavigationSectionProps>(
  ({ sticky = false, children, ...rest }, ref) => {
    const frameProps = rest as NavigationSectionFrameProps;

    if (!sticky) {
      return (
        <NavigationSectionFrame ref={ref} {...frameProps}>
          {children}
        </NavigationSectionFrame>
      );
    }

    return (
      <NavigationSectionSticky ref={ref} {...rest}>
        {children}
      </NavigationSectionSticky>
    );
  },
);

const navigationSharedValues = {
  icon: {
    size: {
      initial: 14,
      minimized: 16,
    },
  },
  label: {
    size: {
      desktop: 13,
      mobile: 14,
    },
    lineHeight: {
      desktop: 16,
      mobile: 18,
    },
  },
  radii: {
    outer: 10,
    inner: 8,
  },
  size: {
    height: 24,
    width: 248,
  },
  zIndices: {
    initial: 1,
    hovered: 2,
    focused: 3,
  },
};

export type NavigationGroupIDProp = string;

export interface StaticNavigationGroupLeafProps<Path extends string = string> {
  accessLevel?: AccessLevelProp | null;
  badge?: Omit<BadgeProps, 'size'>;
  icon?: IconName;
  label: ReactNode;
  subgroup?: string;

  path: Path;
  end?: boolean;
  linkArgs?: Omit<ExtractRouteParams<Path, string>, 'companyName' | 'networkSlug'>;
  selected?: boolean;
}

export interface StaticNavigationGroupLeafGroupProps {
  group: string;
  accessLevel?: AccessLevelProp | null;
  leaves: StaticNavigationGroupLeafProps[];
}

export interface StaticNavigationGroupRootProps<Path extends string = string>
  extends StaticNavigationGroupLeafProps<Path> {
  onClick?: () => void;
}

export interface StaticNavigationGroupProps {
  linkArgs?: {
    companyName: string;
    networkSlug: string;
  };

  /**
   * The ID of the group.
   */
  id: NavigationGroupIDProp;
  /**
   * The root of the group.
   */
  root: StaticNavigationGroupRootProps;
  /**
   * The leaves of the group. Can be individual leaves or grouped leaves.
   */
  leaves?: (StaticNavigationGroupLeafProps | StaticNavigationGroupLeafGroupProps)[];
}

export interface NavigationGroupLeafProps {
  accessLevel?: AccessLevelProp | null;
  badge?: Omit<BadgeProps, 'size'>;
  icon?: IconName;
  label: ReactNode;
  subgroup?: string;

  /** If omitted and `path` provided, will use route matching. */
  selected?: boolean;

  // Routing
  as?: React.ElementType;
  to?: To;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  rel?: string;
}

export interface NavigationGroupLeafGroupProps {
  group: string;
  accessLevel?: AccessLevelProp | null;
  leaves: NavigationGroupLeafProps[];
}

export interface NavigationGroupRootProps extends NavigationGroupLeafProps {
  onClick?: () => void;
}

export type NavigationGroupSize = 'medium' | 'large' | 'x-large';

export interface NavigationGroupProps {
  /**
   * The ID of the group.
   */
  id: NavigationGroupIDProp;
  /**
   * The root of the group.
   */
  root: NavigationGroupRootProps;
  /**
   * The leaves of the group. Can be individual leaves or grouped leaves.
   */
  leaves?: (NavigationGroupLeafProps | NavigationGroupLeafGroupProps)[];
  /**
   * Whether to show the root.
   *
   * @default true
   */
  showRoot?: boolean;
  /**
   * Whether the dock is minimized.
   */
  minimized?: boolean;
  /**
   * The size of the group's targets.
   */
  size?: NavigationGroupSize;
}

const NavigationGroupRootIcon = styled(Icon, {
  color: '$$navigationGroupRootIcon',

  variants: {
    minimized: {
      true: {
        widthAll: `$${navigationSharedValues.icon.size.minimized}`,
        heightAll: `$${navigationSharedValues.icon.size.minimized}`,
      },
      false: {
        widthAll: `$${navigationSharedValues.icon.size.initial}`,
        heightAll: `$${navigationSharedValues.icon.size.initial}`,
      },
    },
  },
});
NavigationGroupRootIcon.displayName = 'NavigationGroupRootIcon';

const NavigationGroupRootLabel = styled(Body, {
  width: '100%',
  fontWeight: '$bold',
  whiteSpace: 'nowrap',

  variants: {
    minimized: {
      true: {
        display: '$$navigationGroupMinimizedDisplay',
        opacity: '$$navigationGroupMinimizedOpacity',
      },
      false: {},
    },
    selected: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      selected: true,
      internal: false,
      css: {
        color: colors.headingBrandLight,
        [darkThemeSelector]: {
          color: colors.headingBrandDark,
        },
      },
    },
    {
      selected: true,
      internal: true,
      css: {
        color: colors.internalHeadingLight,
        [darkThemeSelector]: {
          color: colors.internalHeadingDark,
        },
      },
    },
  ],
});
NavigationGroupRootLabel.displayName = 'NavigationGroupRootLabel';

const NavigationGroupRootBadges = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',

  variants: {
    minimized: {
      true: {
        display: '$$navigationGroupMinimizedDisplay',
        opacity: '$$navigationGroupMinimizedOpacity',
      },
      false: {},
    },
  },
});
NavigationGroupRootBadges.displayName = 'NavigationGroupRootBadges';

const NavigationGroupRootChevron = styled(UtilityControl, {
  marginRight: '$4',

  '@notDesktop': {
    widthAll: '$28',
    heightAll: '$28',
  },

  '@desktop': {
    widthAll: '$24',
    heightAll: '$24',
  },

  variants: {
    collapsed: {
      true: {
        transform: 'rotate(-90deg)',
      },
      false: {},
    },
  },
});
NavigationGroupRootChevron.displayName = 'NavigationGroupRootChevron';

const NavigationGroupRootTarget = styled('div', {
  position: 'relative',
  zIndex: navigationSharedValues.zIndices.initial,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
  width: '100%',
  padding: '$4 $6',
  transition: `${transitions.control}`,

  variants: {
    size: {
      medium: {},
      large: {},
      'x-large': {},
    },
    isPressable: {
      true: {
        cursor: 'pointer',
      },
      false: {},
    },
    minimized: {
      true: {
        borderRadius: '$8',
        heightAll: `$${navigationSharedValues.size.height}`,
      },
      false: {
        borderRadius: `$${navigationSharedValues.radii.outer}`,
      },
    },
    selected: {
      true: {},
      false: {},
    },
    hovered: {
      true: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
      false: {},
    },
    focused: {
      true: {
        zIndex: navigationSharedValues.zIndices.focused,
        boxShadow: shadows.focusRingLight,
        outline: 'none',

        [darkThemeSelector]: {
          boxShadow: shadows.focusRingDark,
        },
      },
      false: {},
    },
    interactive: {
      true: {
        $$navigationGroupOpacity: 1,
        $$navigationGroupDisplay: 'flex',
      },
      false: {},
    },
    internal: {
      true: {
        $$navigationGroupRootIcon: colors.internalIconLight,

        [darkThemeSelector]: {
          $$navigationGroupRootIcon: colors.internalIconDark,
        },
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      minimized: true,
      selected: false,
      interactive: true,
      css: {
        background: colors.bgApplicationLight,
        boxShadow: shadows.overlayLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
          boxShadow: shadows.overlayDark,
        },
      },
    },
    {
      selected: true,
      internal: false,
      css: {
        $$navigationGroupRootIcon: colors.bodyBrandLight,

        [darkThemeSelector]: {
          $$navigationGroupRootIcon: colors.bodyBrandDark,
        },
      },
    },
    {
      selected: true,
      internal: true,
      css: {
        $$navigationGroupRootIcon: colors.internalBodyLight,

        [darkThemeSelector]: {
          $$navigationGroupRootIcon: colors.internalBodyDark,
        },
      },
    },
    {
      minimized: true,
      selected: true,
      interactive: false,
      internal: false,
      css: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },
      },
    },
    {
      minimized: true,
      selected: true,
      interactive: false,
      internal: true,
      css: {
        background: colors.internalBgLight,
        strokeAll: colors.internalStrokeLight,

        [darkThemeSelector]: {
          background: colors.internalBgDark,
          strokeAll: colors.internalStrokeDark,
        },
      },
    },
    {
      minimized: true,
      selected: true,
      interactive: true,
      internal: false,
      css: {
        background: colors.bgBrandLight,
        boxShadow: shadows.overlayBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          boxShadow: shadows.overlayBrandDark,
        },
      },
    },
    {
      minimized: true,
      selected: true,
      interactive: true,
      internal: true,
      css: {
        background: colors.internalBgLight,
        boxShadow: shadows.overlayInternalLight,

        [darkThemeSelector]: {
          background: colors.internalBgDark,
          boxShadow: shadows.overlayInternalDark,
        },
      },
    },
    {
      size: 'large',
      minimized: false,
      css: {
        padding: '$8 $12',
      },
    },
    {
      size: 'x-large',
      minimized: false,
      css: {
        padding: '$12 $16',
      },
    },
  ],
});
NavigationGroupRootTarget.displayName = 'NavigationGroupRootTarget';

const NavigationGroupRootStack = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$2',
  width: '100%',
});
NavigationGroupRootStack.displayName = 'NavigationGroupRootStack';

const NavigationGroupRoot = styled('div', {
  display: 'flex',
  width: '100%',

  variants: {
    minimized: {
      true: {
        pointerEvents: 'auto',
      },
      false: {},
    },
    selected: {
      true: {},
      false: {},
    },
    interactive: {
      true: {},
      false: {},
    },
  },
});
NavigationGroupRoot.displayName = 'NavigationGroupRoot';

const NavigationGroupLeafIcon = styled(Icon, {
  color: '$$navigationGroupLeafIcon',
  widthAll: '$14',
  heightAll: '$14',
});
NavigationGroupLeafIcon.displayName = 'NavigationGroupLeafIcon';

const NavigationGroupLeafLabel = styled(Text, {
  color: '$$navigationGroupLeafLabel',
  fontWeight: '$bold',
});
NavigationGroupLeafLabel.displayName = 'NavigationGroupLeafLabel';

const NavigationGroupLeafStack = styled(AlignStack, {
  padding: '$4 $6',
});
NavigationGroupLeafStack.displayName = 'NavigationGroupLeafStack';

const NavigationGroupLeaf = styled('div', {
  position: 'relative',
  zIndex: navigationSharedValues.zIndices.initial,
  display: 'flex',
  width: '100%',
  borderRadius: `$${navigationSharedValues.radii.inner}`,

  variants: {
    size: {
      medium: {},
      large: {},
      'x-large': {},
    },
    minimized: {
      true: {},
      false: {},
    },
    isPressable: {
      true: {
        cursor: 'pointer',
      },
      false: {},
    },
    hovered: {
      true: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
      false: {},
    },
    focused: {
      true: {
        zIndex: navigationSharedValues.zIndices.focused,
        boxShadow: shadows.focusRingLight,
        outline: 'none',

        [darkThemeSelector]: {
          boxShadow: shadows.focusRingDark,
        },
      },
      false: {},
    },
    selected: {
      true: {},
      false: {},
    },
    internal: {
      true: {
        $$navigationGroupLeafIcon: colors.internalIconLight,
        $$navigationGroupLeafLabel: colors.internalBodyLight,

        [darkThemeSelector]: {
          $$navigationGroupLeafIcon: colors.internalIconDark,
          $$navigationGroupLeafLabel: colors.internalBodyDark,
        },
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      selected: true,
      internal: false,
      css: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,

        $$navigationGroupLeafIcon: colors.bodyBrandLight,
        $$navigationGroupLeafLabel: colors.headingBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,

          $$navigationGroupLeafIcon: colors.bodyBrandDark,
          $$navigationGroupLeafLabel: colors.headingBrandDark,
        },
      },
    },
    {
      hovered: true,
      internal: true,
      css: {
        background: colors.internalBgLight,

        [darkThemeSelector]: {
          background: colors.internalBgDark,
        },
      },
    },
    {
      selected: true,
      internal: true,
      css: {
        background: colors.internalBgLight,
        strokeAll: colors.internalStrokeLight,

        $$navigationGroupLeafIcon: colors.internalBodyLight,
        $$navigationGroupLeafLabel: colors.internalHeadingLight,

        [darkThemeSelector]: {
          background: colors.internalBgDark,
          strokeAll: colors.internalStrokeDark,

          $$navigationGroupLeafIcon: colors.internalBodyDark,
          $$navigationGroupLeafLabel: colors.internalHeadingDark,
        },
      },
    },
    {
      size: 'large',
      minimized: false,
      css: {
        padding: '$8 $12',
      },
    },
    {
      size: 'x-large',
      minimized: false,
      css: {
        padding: '$12 $16',
      },
    },
  ],
});
NavigationGroupLeaf.displayName = 'NavigationGroupLeaf';

const NavigationGroupLeavesGroupHeader = styled(Small, {
  fontWeight: '$bold',
  padding: '0 $6',
  color: '$$navigationGroupLeafGroupHeader',
});
NavigationGroupLeavesGroupHeader.displayName = 'NavigationGroupLeavesGroupHeader';

const NavigationGroupLeavesGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  padding: '$2 0 $6',

  '&:last-child': {
    paddingBottom: 0,
  },
});
NavigationGroupLeavesGroup.displayName = 'NavigationGroupLeavesGroup';

const NavigationGroupLeavesDivider = styled(Divider, {
  padding: '$2 $4',
});
NavigationGroupLeavesDivider.displayName = 'NavigationGroupLeavesDivider';

const NavigationGroupLeaves = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  padding: '$4',
  borderRadius: `$${navigationSharedValues.radii.inner}`,
  transition: `${transitions.control}, opacity 0.2s ease-in-out, transform 0.2s ease-in-out`,

  variants: {
    collapsed: {
      true: {
        display: 'none',
      },
      false: {},
    },
    minimized: {
      true: {
        pointerEvents: 'auto',
        margin: '$4 0 0 $40',

        display: '$$navigationGroupMinimizedDisplay',
        opacity: '$$navigationGroupMinimizedOpacity',
      },
      false: {},
    },
    shouldShowMinimized: {
      true: {
        display: 'flex',
        opacity: 1,
      },
      false: {},
    },
    selected: {
      true: {},
      false: {},
    },
    interactive: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    showRoot: {
      true: {
        background: colors.bgApplicationLight,
        strokeAll: colors.strokeNeutralLight,
        $$navigationGroupLeafGroupHeader: colors.bodyNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
          strokeAll: colors.strokeNeutralDark,
          $$navigationGroupLeafGroupHeader: colors.bodyNeutralDark,
        },
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      internal: true,
      css: {
        $$navigationGroupLeafGroupHeader: colors.internalBodyLight,

        [darkThemeSelector]: {
          $$navigationGroupLeafGroupHeader: colors.internalBodyDark,
        },
      },
    },
    {
      selected: true,
      internal: false,
      css: {
        strokeAll: colors.strokeBrandLight,

        [darkThemeSelector]: {
          strokeAll: colors.strokeBrandDark,
        },
      },
    },
    {
      selected: true,
      internal: true,
      css: {
        strokeAll: colors.internalStrokeLight,

        [darkThemeSelector]: {
          strokeAll: colors.internalStrokeDark,
        },
      },
    },
    {
      minimized: true,
      selected: false,
      interactive: true,
      css: {
        boxShadow: shadows.overlayLight,

        [darkThemeSelector]: {
          boxShadow: shadows.overlayDark,
        },
      },
    },
    {
      minimized: true,
      selected: true,
      interactive: true,
      internal: false,
      css: {
        boxShadow: shadows.overlayBrandLight,

        [darkThemeSelector]: {
          boxShadow: shadows.overlayBrandDark,
        },
      },
    },
    {
      minimized: true,
      selected: true,
      interactive: true,
      internal: true,
      css: {
        boxShadow: shadows.overlayInternalLight,

        [darkThemeSelector]: {
          boxShadow: shadows.overlayInternalDark,
        },
      },
    },
  ],
});
NavigationGroupLeaves.displayName = 'NavigationGroupLeaves';

const NavigationGroupTree = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  background: colors.transparent,
  strokeAll: colors.transparent,
  borderRadius: `$${navigationSharedValues.radii.outer}`,
  transition: `${transitions.control}, opacity 0.2s ease-in-out, transform 0.2s ease-in-out`,
  $$navigationGroupMinimizedDisplay: 'none',
  $$navigationGroupMinimizedOpacity: 0,

  variants: {
    showRoot: {
      false: {},
      true: {},
    },
    collapsed: {
      true: {},
      false: {},
    },
    hasLeaves: {
      true: {},
      false: {},
    },
    minimized: {
      true: {
        pointerEvents: 'auto',
      },
      false: {},
    },
    selected: {
      true: {},
      false: {},
    },
    shouldShowMinimized: {
      true: {
        position: 'fixed',
        width: `calc($${navigationSharedValues.size.width} + $8)`,
        pointerEvents: 'none',
        $$navigationGroupMinimizedDisplay: 'block',
        $$navigationGroupMinimizedOpacity: 1,
        zIndex: zIndexes.dockOverlay,
        visibility: 'hidden',

        '& > *': {
          pointerEvents: 'auto',
        },
      },
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      minimized: false,
      collapsed: false,
      hasLeaves: true,
      selected: false,
      internal: false,
      showRoot: true,
      css: {
        background: colors.bgNeutralLight,
        strokeAll: colors.strokeNeutralLight,
        $$navigationGroupRootIcon: colors.iconNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
          strokeAll: colors.strokeNeutralDark,
          $$navigationGroupRootIcon: colors.iconNeutralDark,
        },
      },
    },
    {
      minimized: false,
      collapsed: false,
      hasLeaves: true,
      selected: false,
      internal: true,
      showRoot: true,
      css: {
        background: colors.bgNeutralLight,
        strokeAll: colors.strokeNeutralLight,
        $$navigationGroupRootIcon: colors.internalIconLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
          strokeAll: colors.strokeNeutralDark,
          $$navigationGroupRootIcon: colors.internalIconDark,
        },
      },
    },
    {
      minimized: false,
      selected: true,
      internal: false,
      showRoot: true,
      css: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,
        $$navigationGroupRootIcon: colors.bodyBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
          $$navigationGroupRootIcon: colors.bodyBrandDark,
        },
      },
    },
    {
      minimized: false,
      selected: true,
      internal: true,
      showRoot: true,
      css: {
        background: colors.internalBgLight,
        strokeAll: colors.internalStrokeLight,
        $$navigationGroupRootIcon: colors.internalBodyLight,

        [darkThemeSelector]: {
          background: colors.internalBgDark,
          strokeAll: colors.internalStrokeDark,
          $$navigationGroupRootIcon: colors.internalBodyDark,
        },
      },
    },
    {
      minimized: true,
      selected: true,
      showRoot: true,
      css: {},
    },
  ],
});
NavigationGroupTree.displayName = 'NavigationGroupTree';

const NavigationGroupContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  variants: {
    minimized: {
      true: {
        heightAll: `$${navigationSharedValues.size.height}`,
      },
      false: {},
    },
  },
});
NavigationGroupContainer.displayName = 'NavigationGroupContainer';

// Memoized leaf component to prevent unnecessary re-renders
function NavigationLeafComponent({
  leaf,
  groupName,
  hoveredLeafKey,
  focusedLeafKey,
  onLeafHover,
  size = 'medium',
  minimized = false,
}: {
  leaf: NavigationGroupLeafProps;
  groupName?: string;
  hoveredLeafKey: string | null;
  focusedLeafKey: string | null;
  onLeafHover: (key: string | null) => void;
  size?: NavigationGroupSize;
  minimized?: boolean;
}) {
  const leafIsPressable = leaf && leaf.as && leaf.to;
  const leafKey = `${groupName ? `${groupName}-` : ''}${leaf.label?.toString() ?? ''}`;
  const isLeafHovered = hoveredLeafKey === leafKey;
  const isLeafFocused = focusedLeafKey === leafKey;
  const isLeafInternal = leaf.accessLevel === AccessLevel.Internal;

  if (leaf.accessLevel === null) {
    return null;
  }

  return (
    <NavigationGroupLeaf
      size={size}
      key={leafKey}
      as={leaf.as}
      to={leaf.to}
      href={leaf.href}
      target={leaf.target}
      rel={leaf.rel}
      tabIndex={leafIsPressable ? 0 : undefined}
      isPressable={!!leafIsPressable}
      selected={leaf.selected}
      hovered={isLeafHovered}
      focused={isLeafFocused}
      internal={isLeafInternal}
      minimized={minimized}
      data-leaf-key={leafKey}
      onMouseEnter={() => onLeafHover(leafKey)}
      onMouseLeave={() => onLeafHover(null)}
      role="listitem"
      aria-current={leaf.selected ? 'page' : undefined}
    >
      <NavigationGroupLeafStack
        gap={8}
        preset="body"
        width="100%"
        start={leaf.icon && <NavigationGroupLeafIcon icon={leaf.icon} />}
        end={
          <>
            {leaf.accessLevel && <FeatureBadge type={leaf.accessLevel} size="x-small" />}
            {leaf.badge && (
              <Badge
                ends="pill"
                size="small"
                {...leaf.badge}
                variant={leaf.selected ? 'brand' : leaf.badge.variant}
              />
            )}
          </>
        }
      >
        <NavigationGroupLeafLabel>{leaf.label}</NavigationGroupLeafLabel>
      </NavigationGroupLeafStack>
    </NavigationGroupLeaf>
  );
}

const NavigationLeaf = memo(NavigationLeafComponent);

function NavigationGroupComponent({
  id,
  root,
  leaves,
  showRoot = true,
  minimized = false,
  size = 'medium',
}: NavigationGroupProps) {
  const accessLevel = useAccessLevelContext(root.accessLevel);
  const isInternal = useMemo(() => accessLevel === AccessLevel.Internal, [accessLevel]);

  const {
    collapsed: contextCollapsed,
    setCollapsed: contextSetCollapsed,
    collapsible,
    openGroupId,
    setOpenGroupId,
    focusedGroupId,
    setFocusedGroupId,
  } = useNavigationGroupsContext();

  // Memoize expensive computations
  const rootIsPressable = Boolean(root.as && root.to);
  const rootIsSelected = Boolean(root?.selected);

  // Create a stable reference for leaf selection state to help with memoization
  const leafSelectionState = useMemo(() => {
    if (!leaves?.length) return '';

    return leaves
      .map((leaf) => {
        if ('group' in leaf) {
          return leaf.leaves.map((l) => `${l.label}-${l.selected}`).join('|');
        }
        return `${leaf.label}-${leaf.selected}`;
      })
      .join('||');
  }, [leaves]);

  const hasLeaves = Boolean(leaves?.length);

  const hasSelectedLeaf = useMemo(() => {
    if (!hasLeaves) return false;
    const result = leaves?.some((leaf) => {
      if ('group' in leaf) {
        return leaf.leaves.some((l) => l.selected);
      }
      return leaf.selected;
    });

    return result;
  }, [hasLeaves, leaves]);

  const shouldShowRootAsSelected = showRoot && (rootIsSelected || hasSelectedLeaf);

  // Force re-render when selection state changes to ensure proper styling
  const [, forceUpdate] = useState({});
  useEffect(() => {
    forceUpdate({});
  }, [leafSelectionState, rootIsSelected, minimized]);

  const collapsed = collapsible ? !rootIsSelected && contextCollapsed : !rootIsSelected;

  // Consolidate state to reduce re-renders
  const [interactionState, setInteractionState] = useState<{
    isHovered: boolean;
    isFocused: boolean;
    isRootFocused: boolean;
    isRootHovered: boolean;
    isLeavesHovered: boolean;
    hoveredLeafKey: string | null;
    focusedLeafKey: string | null;
  }>({
    isHovered: false,
    isFocused: false,
    isRootFocused: false,
    isRootHovered: false,
    isLeavesHovered: false,
    hoveredLeafKey: null,
    focusedLeafKey: null,
  });

  const groupRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const leavesRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Optimized event handlers with stable references
  const handleFocusIn = useCallback(
    (event: FocusEvent) => {
      if (groupRef.current?.contains(event.target as Node)) {
        setInteractionState((prev) => ({ ...prev, isFocused: true }));
        setFocusedGroupId(id);

        const focusedElement = event.target as HTMLElement;
        const rootTarget = rootRef.current?.querySelector('[data-navigation-target]');
        const isRootTarget =
          focusedElement === rootTarget ||
          focusedElement.closest('[data-navigation-target]') === rootTarget;

        if (isRootTarget) {
          setInteractionState((prev) => ({ ...prev, isRootFocused: true, focusedLeafKey: null }));
        } else {
          const leafKey = focusedElement.getAttribute('data-leaf-key');
          setInteractionState((prev) => ({
            ...prev,
            isRootFocused: false,
            focusedLeafKey: leafKey,
          }));
        }

        if (minimized) {
          setOpenGroupId(id);
        }
      }
    },
    [id, minimized, setFocusedGroupId, setOpenGroupId],
  );

  const handleFocusOut = useCallback(
    (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget as Node | null;

      if (!relatedTarget || !groupRef.current?.contains(relatedTarget)) {
        setInteractionState((prev) => ({
          ...prev,
          isFocused: false,
          isRootFocused: false,
          focusedLeafKey: null,
        }));
        if (focusedGroupId === id) {
          setFocusedGroupId(null);
        }
      } else {
        const focusedElement = event.target as HTMLElement;
        const rootTarget = rootRef.current?.querySelector('[data-navigation-target]');
        const isRootTarget =
          focusedElement === rootTarget ||
          focusedElement.closest('[data-navigation-target]') === rootTarget;

        if (!isRootTarget) {
          setInteractionState((prev) => ({ ...prev, isRootFocused: false }));
        }
      }
    },
    [focusedGroupId, id, setFocusedGroupId],
  );

  useEffect(() => {
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [handleFocusIn, handleFocusOut]);

  // Optimized effects with consolidated state updates
  useEffect(() => {
    if (openGroupId !== null && openGroupId !== id) {
      setInteractionState((prev) => ({
        ...prev,
        isHovered: false,
        isRootHovered: false,
        isLeavesHovered: false,
      }));

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [openGroupId, id]);

  useEffect(() => {
    if (focusedGroupId !== null && focusedGroupId !== id) {
      setInteractionState((prev) => ({
        ...prev,
        isFocused: false,
        isRootFocused: false,
        isRootHovered: false,
        focusedLeafKey: null,
        hoveredLeafKey: null,
      }));
    }
  }, [focusedGroupId, id]);

  // Stable event handlers
  const handleGroupMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setInteractionState((prev) => ({ ...prev, isHovered: true }));

    if (minimized) {
      setOpenGroupId(id);
    }
  }, [minimized, setOpenGroupId, id]);

  const handleGroupMouseLeave = useCallback(
    (event: React.MouseEvent) => {
      const target = event.relatedTarget;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const isMovingToAnotherGroup =
        target &&
        (target.closest('[data-navigation-group]') !== groupRef.current ||
          target.closest('[data-navigation-root]'));

      const delay = isMovingToAnotherGroup ? 400 : 500;

      timeoutRef.current = setTimeout(() => {
        setInteractionState((prev) => ({
          ...prev,
          isHovered: false,
          isLeavesHovered: false,
        }));
        if (openGroupId === id) {
          setOpenGroupId(null);
        }
      }, delay);
    },
    [openGroupId, id, setOpenGroupId],
  );

  const handleLeavesMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setInteractionState((prev) => ({ ...prev, isLeavesHovered: true }));
  }, []);

  const handleLeavesMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setInteractionState((prev) => ({ ...prev, isLeavesHovered: false }));
    }, 500);
  }, []);

  const handleLeafHover = useCallback((key: string | null) => {
    setInteractionState((prev) => ({ ...prev, hoveredLeafKey: key }));
  }, []);

  const handleRootMouseEnter = useCallback(() => {
    setInteractionState((prev) => ({ ...prev, isRootHovered: true }));
  }, []);

  const handleRootMouseLeave = useCallback(() => {
    setInteractionState((prev) => ({ ...prev, isRootHovered: false }));
  }, []);

  // Memoized computed values
  const shouldShowMinimized = useMemo(() => {
    if (!minimized) return false;

    if (hasLeaves) {
      return (
        (interactionState.isHovered ||
          interactionState.isFocused ||
          interactionState.isLeavesHovered) &&
        openGroupId === id
      );
    }

    return (interactionState.isHovered || interactionState.isFocused) && openGroupId === id;
  }, [
    minimized,
    hasLeaves,
    interactionState.isHovered,
    interactionState.isFocused,
    interactionState.isLeavesHovered,
    openGroupId,
    id,
  ]);

  const calculatePosition = useCallback(() => {
    if (
      minimized &&
      (interactionState.isHovered || interactionState.isFocused) &&
      groupRef.current
    ) {
      const rect = groupRef.current.getBoundingClientRect();
      return {
        top: rect.top,
        left: rect.left,
      };
    }
    return null;
  }, [minimized, interactionState.isHovered, interactionState.isFocused]);

  const currentFixedPosition = useMemo(() => {
    if (shouldShowMinimized) {
      return calculatePosition();
    }
    return null;
  }, [shouldShowMinimized, calculatePosition]);

  if (root.accessLevel === null) {
    return null;
  }

  return (
    <UserAccessLevelContextProvider value={accessLevel}>
      <NavigationGroupContainer
        id={id}
        minimized={minimized}
        role="group"
        aria-label={`${root?.label} group`}
      >
        <NavigationGroupTree
          ref={groupRef}
          collapsed={collapsed}
          minimized={minimized}
          selected={!!shouldShowRootAsSelected}
          hasLeaves={hasLeaves}
          shouldShowMinimized={shouldShowMinimized}
          internal={isInternal}
          onMouseEnter={handleGroupMouseEnter}
          onMouseLeave={handleGroupMouseLeave}
          showRoot={showRoot}
          data-navigation-group
          style={
            currentFixedPosition
              ? {
                  top: `${currentFixedPosition.top}px`,
                  left: `${currentFixedPosition.left}px`,
                  visibility: 'visible',
                }
              : undefined
          }
        >
          {root && showRoot && (
            <NavigationGroupRoot
              ref={rootRef}
              minimized={minimized}
              selected={shouldShowRootAsSelected}
              interactive={interactionState.isHovered || interactionState.isFocused}
              data-navigation-root
              id={`${id}-root`}
            >
              <NavigationGroupRootStack>
                <NavigationGroupRootTarget
                  as={root.as}
                  to={root.to}
                  href={root.href}
                  target={root.target}
                  rel={root.rel}
                  size={size}
                  tabIndex={rootIsPressable ? 0 : undefined}
                  isPressable={!!rootIsPressable}
                  minimized={minimized}
                  selected={shouldShowRootAsSelected}
                  hovered={interactionState.isRootHovered && !shouldShowRootAsSelected}
                  focused={interactionState.isRootFocused}
                  interactive={interactionState.isHovered || interactionState.isFocused}
                  internal={isInternal}
                  aria-label={minimized ? root.label : undefined}
                  data-navigation-target
                  onMouseEnter={handleRootMouseEnter}
                  onMouseLeave={handleRootMouseLeave}
                >
                  {root.icon && <NavigationGroupRootIcon icon={root.icon} minimized={minimized} />}
                  <NavigationGroupRootLabel
                    minimized={minimized}
                    internal={isInternal}
                    selected={shouldShowRootAsSelected}
                  >
                    {root.label}
                  </NavigationGroupRootLabel>
                  <NavigationGroupRootBadges minimized={minimized}>
                    {root.badge && (
                      <Badge
                        ends="pill"
                        size="small"
                        {...root.badge}
                        variant={shouldShowRootAsSelected ? 'brand' : root.badge.variant}
                      />
                    )}
                    {accessLevel && <FeatureBadge size="x-small" type={accessLevel} />}
                  </NavigationGroupRootBadges>
                </NavigationGroupRootTarget>
                {!minimized && !rootIsSelected && hasLeaves && collapsible && (
                  <NavigationGroupRootChevron
                    collapsed={collapsed}
                    onClick={() => contextSetCollapsed(!contextCollapsed)}
                    icon="chevron-down"
                    label={collapsed ? 'Expand' : 'Collapse'}
                    variant={collapsed ? 'application' : 'neutral'}
                    internal={isInternal}
                    aria-expanded={!collapsed}
                    aria-controls={`${id}-leaves`}
                    aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${root?.label} navigation group`}
                  />
                )}
              </NavigationGroupRootStack>
            </NavigationGroupRoot>
          )}
          {hasLeaves && (
            <NavigationGroupLeaves
              ref={leavesRef}
              showRoot={showRoot}
              collapsed={collapsed}
              minimized={minimized}
              selected={!!shouldShowRootAsSelected}
              interactive={
                interactionState.isHovered ||
                interactionState.isLeavesHovered ||
                interactionState.isFocused
              }
              shouldShowMinimized={shouldShowMinimized}
              internal={isInternal}
              onMouseEnter={handleLeavesMouseEnter}
              onMouseLeave={handleLeavesMouseLeave}
              role="list"
              aria-labelledby={`${id}-root`}
              id={`${id}-leaves`}
              aria-hidden={collapsed}
            >
              {leaves?.flatMap((leaf, index) => {
                const elements: React.ReactElement[] = [];

                // Add divider if subgroup changes (for non-grouped leaves)
                if (index > 0 && !('group' in leaf)) {
                  const prevLeaf = leaves[index - 1];
                  if (!('group' in prevLeaf) && prevLeaf.subgroup !== leaf.subgroup) {
                    const dividerKey = `divider-${prevLeaf.subgroup ?? 'none'}-${leaf.subgroup ?? 'none'}`;
                    elements.push(<NavigationGroupLeavesDivider key={dividerKey} />);
                  }
                }

                if ('group' in leaf) {
                  elements.push(
                    <NavigationGroupLeavesGroup key={leaf.group}>
                      <NavigationGroupLeavesGroupHeader>
                        {leaf.group}
                      </NavigationGroupLeavesGroupHeader>
                      {leaf.leaves.map((l) => (
                        <NavigationLeaf
                          size={size}
                          key={`${leaf.group}-${l.label?.toString() ?? ''}`}
                          leaf={l}
                          minimized={minimized}
                          groupName={leaf.group}
                          hoveredLeafKey={interactionState.hoveredLeafKey}
                          focusedLeafKey={interactionState.focusedLeafKey}
                          onLeafHover={handleLeafHover}
                        />
                      ))}
                    </NavigationGroupLeavesGroup>,
                  );
                } else {
                  elements.push(
                    <NavigationLeaf
                      key={leaf.label?.toString() ?? ''}
                      leaf={leaf}
                      minimized={minimized}
                      hoveredLeafKey={interactionState.hoveredLeafKey}
                      focusedLeafKey={interactionState.focusedLeafKey}
                      onLeafHover={handleLeafHover}
                    />,
                  );
                }

                return elements;
              })}
            </NavigationGroupLeaves>
          )}
        </NavigationGroupTree>
      </NavigationGroupContainer>
    </UserAccessLevelContextProvider>
  );
}

export const NavigationGroup = memo(NavigationGroupComponent);
